import type { DataOption } from '@/common.types'
import type { FilterRuleModel, PricingCampaignModel } from '@/shared/generated'
import type { FieldOption, UpdateProductsByFieldsSchema } from '../model/types'

import { useCallback, useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { find, prop } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useAssortmentListQuery } from '@/entities/assortment'
import { useColumnsSchemaQuery } from '@/entities/column'
import {
	useUpdateProductsFieldsByFiltersMutation,
	useUpdateProductsFieldsMutation,
} from '@/entities/optimization'
import {
	pcToOption,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import { ValidationErrorsList } from '@/shared/components/common/ValidationErrorsList'
import { FormProvider } from '@/shared/components/HookForm'
import { Operators } from '@/shared/components/query-builder'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import { HiddenOnContext, ValueType } from '@/shared/generated'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { ampli } from '@/shared/lib/ampli'

import { updateProductsByFieldsSchema } from '../model/contract'
import { FieldsList } from './update-by-fields/FieldsList'
import { SkusTextarea } from './update-by-fields/SkusTextarea'

type ProductsBulkModalProps = {
	filters: FilterRuleModel[]
	isOpen: boolean
	close(): void
}

export function ProductsUpdateModal({
	filters,
	isOpen,
	close,
}: ProductsBulkModalProps) {
	const { t } = useTranslation()

	const optimizationGroupId = useOptimizationGroupId()!
	const assortmentQuery = useAssortmentListQuery(
		{
			limit: 1,
			filters: [
				{
					name: 'optimization_group_id',
					operation: Operators.IS,
					value: optimizationGroupId,
				},
				...(filters || []),
			],
		},
		{ enabled: filters?.length > 0 }
	)
	const updateProductsFieldsByFilters =
		useUpdateProductsFieldsByFiltersMutation(optimizationGroupId, {
			onSuccess: () => {
				close()
			},
		})
	const updateProductsFields = useUpdateProductsFieldsMutation(
		optimizationGroupId,
		{
			onSuccess: () => {
				close()
			},
		}
	)

	const fieldsQuery = useColumnsSchemaQuery<FieldOption[]>(
		HiddenOnContext.Products,
		{
			select: (data) =>
				data.filter(prop('params.editable')).map((schema): FieldOption => ({
					value: schema.id,
					label: t(schema.name, { ns: 'fields', defaultValue: schema.name }),
					type: schema.value_type,
					field_type: schema.field_type!,
					schema,
				})),
		}
	)
	const pricingCampaignsQuery = usePricingCampaignsQuery(
		{ optimization_group_id: optimizationGroupId },
		{ select: selectCampaigns }
	)

	const fields = useMemo(() => fieldsQuery.data || [], [fieldsQuery.data])
	const campaigns = useMemo(
		() => pricingCampaignsQuery.data || [],
		[pricingCampaignsQuery.data]
	)

	const byFilters = !!assortmentQuery.data?.total_skus
	const methods = useForm({
		defaultValues: { fields: [], skus: [] },
		resolver: zodResolver(updateProductsByFieldsSchema),
	})
	const getField = useCallback(
		(value: string) => find({ value }, fields || []),
		[fields]
	)
	const getFieldOptions = useCallback(
		(field: FieldOption) => {
			if (!field.schema) return []
			const dataChoices: Record<string, DataOption[]> = {
				pricing_campaign_id: campaigns,
			}

			return (
				dataChoices?.[field.schema.id] ||
				(field.schema.config?.enum || []).map((option) => ({
					value: option,
					label: option,
				}))
			)
		},
		[campaigns]
	)
	const isError =
		updateProductsFields.isError || updateProductsFieldsByFilters.isError

	const errorsList =
		updateProductsFields.error?.response?.data.detail ||
		updateProductsFieldsByFilters.error?.response?.data.detail

	const isLoading =
		pricingCampaignsQuery.isLoading ||
		fieldsQuery.isLoading ||
		assortmentQuery.isLoading

	const isPending =
		updateProductsFields.isPending || updateProductsFieldsByFilters.isPending

	const onSubmit = async (data: UpdateProductsByFieldsSchema) => {
		ampli.tableManualUpdate({
			'Amount of fields for updating': data.fields.length,
		})

		const values = createChanges(data.fields)
		const payload = data.skus.map((sku) => ({ sku, ...values }))
		if (!byFilters) {
			await toast.promise(
				updateProductsFields.mutateAsync({
					changes: payload,
					ignore_empties: false,
				}),
				{
					loading: t('toast.entity.updating', {
						entity: t('entity.products'),
						defaultValue: 'Updating products...',
					}),
					success: t('toast.entity.updated', {
						entity: t('entity.products'),
					}),
					error: t('toast.entity.failed', {
						entity: t('entity.products'),
					}),
				}
			)
		} else {
			await toast.promise(
				updateProductsFieldsByFilters.mutateAsync({
					filters,
					values,
				}),
				{
					loading: t('toast.entity.updating', {
						entity: t('entity.products'),
						defaultValue: 'Updating products...',
					}),
					success: t('toast.entity.updated', {
						entity: t('entity.products'),
					}),
					error: t('toast.entity.failed', {
						entity: t('entity.products'),
					}),
				}
			)
		}
	}
	const skusNumber = assortmentQuery.data?.total_skus

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent
				className={
					isLoading || isPending
						? 'max-w-sm'
						: byFilters
							? 'max-w-3xl'
							: 'max-w-5xl'
				}
			>
				<FormProvider
					methods={methods}
					onSubmit={methods.handleSubmit(onSubmit)}
					className='flex flex-col gap-4'
				>
					{isLoading ? (
						<LoadingContent />
					) : updateProductsFields.isPending ||
					  updateProductsFieldsByFilters.isPending ? (
						<PendingContent />
					) : (
						<div className='flex gap-4'>
							{!byFilters && <SkusTextarea />}
							<div className='flex w-full flex-col gap-4'>
								<ModalHeader>
									<ModalTitle>
										{t('bulk_modal_bulk_update') +
											' ' +
											(skusNumber ? `(${skusNumber})` : '')}
									</ModalTitle>
									<ModalDescription>
										{t('bulk_modal_bulk_update_description')}
									</ModalDescription>
								</ModalHeader>
								<ModalBody className='max-h-[90vh] min-h-75'>
									<FieldsList
										fields={fields || []}
										getField={getField}
										getFieldOptions={getFieldOptions}
									/>
									{isError &&
										(errorsList?.length ? (
											<>
												<Alert variant='destructive' className='mt-4'>
													<AlertTriangleIcon />
													<AlertDescription>
														{t('common.error.request_failed_description', {
															defaultValue:
																'The request failed. Please try again.',
														})}
													</AlertDescription>
												</Alert>
												<div className='mt-2 mb-auto flex w-full flex-col gap-2'>
													<ValidationErrorsList errors={errorsList} />
												</div>
											</>
										) : (
											<Alert variant='destructive' className='mt-5'>
												<AlertTriangleIcon />
												<AlertDescription>
													{t('error.unrecoverable.title')}
												</AlertDescription>
											</Alert>
										))}
								</ModalBody>
								<ModalFooter className='justify-between'>
									<ModalClose asChild>
										<Button data-testid='products-bulk-update-modal-cancel-button'>
											{t('general_cancel')}
										</Button>
									</ModalClose>
									<Button
										variant='primary'
										onClick={methods.handleSubmit(onSubmit)}
										data-testid='products-bulk-update-modal-apply-button'
									>
										{t('general_apply')}
									</Button>
								</ModalFooter>
							</div>
						</div>
					)}
				</FormProvider>
			</ModalContent>
		</Modal>
	)
}

const LoadingContent = () => {
	const { t } = useTranslation()

	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<Spinner className='text-muted-foreground' />
				</EmptyMedia>
				<EmptyTitle>
					{t('bulk_modal_bulk_update_loading', {
						defaultValue: 'Preparing data...',
					})}
				</EmptyTitle>
				<EmptyDescription>
					{t('bulk_modal_bulk_update_loading_description', {
						defaultValue:
							'We are preparing the data for the update. Please wait a moment.',
					})}
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	)
}
const PendingContent = () => {
	const { t } = useTranslation()

	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<Spinner className='text-muted-foreground' />
				</EmptyMedia>
				<EmptyTitle>
					{t('bulk_modal_bulk_update_pending', {
						defaultValue: 'Updating products...',
					})}
				</EmptyTitle>
				<EmptyDescription>
					{t('bulk_modal_bulk_update_pending_description', {
						defaultValue:
							'This may take a few minutes. Please wait for the process to complete.',
					})}
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	)
}
const selectCampaigns = (data: PricingCampaignModel[]) => data.map(pcToOption)

const createChanges = (
	fields: {
		value: string
		inputValue: string | boolean
		type?: string
	}[]
) =>
	Object.fromEntries(
		fields.map((item) => [
			item.value,
			item.type === ValueType.Boolean ? !!item.inputValue : item.inputValue,
		])
	)
